import React from 'react'
import { graphql } from 'relay-runtime'
import styled from '@emotion/styled'
import { useFragment } from 'react-relay'
import { Comment$key } from './__generated__/Comment.graphql'
import { IconButton, Menu, MenuItem } from '@mui/material'
import { MoreVert } from '@mui/icons-material'
import { toHumanReadableDate } from '../../common'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import { selectAuth, setDeleteCommentConfirmDialog } from '../../state/appSlice'
import { theme } from '../../mui'

interface CommentProps extends CommentContainerProps {
  userId: string
}

export const commentFragment = graphql`
  fragment Comment on Comment {
    id
    createdAt
    text
    user {
      id
      fullName
      picture
    }
  }
`

const S = {
  Wrapper: styled.div`
    width: 20rem;
    display: flex;
  `,
  Image: styled.img`
    border-radius: 50%;
    height: 2.5rem;
  `,
  Comment: styled.div`
    flex-grow: 1;
    margin-left: 1rem;
  `,
  Header: styled.div`
    display: flex;
    justify-content: space-between;
  `,
  Name: styled.div`
    font-weight: bold;
    margin-bottom: 0.25rem;
  `,
  Date: styled.div`
    font-size: 0.75rem;
    color: ${theme.palette.text.disabled};
    margin-bottom: 0.25rem;
  `,
}

const Comment = ({ fragmentRequest, userId }: CommentProps) => {
  const comment = useFragment(commentFragment, fragmentRequest)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const dispatch = useAppDispatch()

  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const deleteComment = () => {
    dispatch(
      setDeleteCommentConfirmDialog({ open: true, objectId: comment.id })
    )
    setAnchorEl(null)
  }

  return (
    <>
      <S.Wrapper>
        <div>
          <S.Image
            referrerPolicy="no-referrer"
            src={comment.user.picture!}
            alt={comment.user.fullName!}
          />
        </div>
        <S.Comment>
          <S.Header>
            <div>
              <S.Name>{comment.user.fullName}</S.Name>
              <S.Date>{toHumanReadableDate(comment.createdAt)}</S.Date>
            </div>
            {comment.user.id === userId && (
              <IconButton
                sx={{ color: theme.palette.primary.main }}
                onClick={handleClick}
              >
                <MoreVert />
              </IconButton>
            )}
          </S.Header>
          {comment.text}
        </S.Comment>
      </S.Wrapper>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={deleteComment}>Delete</MenuItem>
      </Menu>
    </>
  )
}

export default Comment

interface CommentContainerProps {
  fragmentRequest: Comment$key
}

export const CommentContainer = (props: CommentContainerProps) => {
  const auth = useAppSelector(selectAuth)
  return <Comment userId={auth!.id} {...props} />
}
